import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useVersionedApi } from '../utils/versionedApi';
import { safeStorage } from '../utils/safeStorage';
import { useAuth } from '../AuthContext';
import { isRestrictedAccount } from '../utils/accountKind';

const FeatureVisibilityContext = createContext({
  isVisible: () => true,
  visibilityLoaded: false,
  config: {}
});

export function FeatureVisibilityProvider({ children }) {
  const [config, setConfig] = useState({});
  const [loaded, setLoaded] = useState(false);
  const apiClient = useVersionedApi('v1');
  const { user } = useAuth();

  useEffect(() => {
    const fetchConfig = async () => {
      const token = safeStorage.getItem('evv_token');
      if (!token) {
        setLoaded(true);
        return;
      }

      // Feature visibility is per-organization. A platform admin (organizationId
      // null) cannot pass tenantMiddleware, so this only ever 401s for them.
      if ((user && user.organizationId === null) || isRestrictedAccount(user)) {
        setLoaded(true);
        return;
      }

      try {
        const response = await apiClient.get('/admin/feature-visibility/my-config', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.success) {
          setConfig(response.data.config || {});
        }
      } catch (err) {
        console.warn('Feature visibility config not loaded:', err.message);
      } finally {
        setLoaded(true);
      }
    };

    fetchConfig();
  }, [apiClient, user]);

  const isVisible = useCallback((featurePath) => {
    const parts = featurePath.split('.');
    let current = config;
    for (const part of parts) {
      if (current === undefined || current === null) return true;
      current = current[part];
    }
    return current !== false;
  }, [config]);

  return (
    <FeatureVisibilityContext.Provider value={{ isVisible, visibilityLoaded: loaded, config }}>
      {children}
    </FeatureVisibilityContext.Provider>
  );
}

export const useFeatureVisibility = () => useContext(FeatureVisibilityContext);
